import { ApolloClient, FetchResult, NormalizedCacheObject } from '@apollo/client'
import { GraphQLError } from 'graphql'
import Cookies from 'universal-cookie'

import {
  CartLineInput,
  CartUserError,
  CheckoutCartCreateMutation,
  CustomAttribute,
  FigsCart,
} from '../../__generated__/graphql/catalog/graphql'
import { reportClientError } from '../../app/_components/chrome/scripts/DataDogRumScript'
import { convertCookiesToAccountAuthorization } from '../../app/_config/Authentication.config'
import { cookieKeys } from '../../app/_config/Cookies.config'
import { sessionStorageKeys } from '../../app/_config/Session.config'
import { CheckoutLineItemInput } from '../../types/shopify-storefront-api'
import { CART_ATTRIBUTES_UPDATE } from '../graphql/mutations/CART_ATTRIBUTES_UPDATE'
import { CART_CREATE } from '../graphql/mutations/CART_CREATE'
import { CART_LINES_ADD } from '../graphql/mutations/CART_LINES_ADD'
import { CART_LINES_UPDATE } from '../graphql/mutations/CART_LINES_UPDATE'
import { CHECKOUT_CART_CREATE } from '../graphql/mutations/CHECKOUT_CART_CREATE'
import { FETCH_CART, FETCH_CART_EXISTS } from '../graphql/queries/FETCH_CART'
import { FETCH_CHECKOUT_URL } from '../graphql/queries/FETCH_CHECKOUT_URL'

import { ICheckoutClient } from './interfaces'
import { IterableKeys } from './types'

type ErrorContext = {
  scope: string
  errors: readonly GraphQLError[] | undefined
  userErrors: CartUserError[] | null | undefined
}

export class CartServiceApiClient implements ICheckoutClient {
  private cookies = new Cookies()
  private account = convertCookiesToAccountAuthorization({
    authCookie: this.cookies.get(cookieKeys.authToken.key) ?? undefined,
    authRefreshCookie: this.cookies.get(cookieKeys.authRefreshToken.key) ?? undefined,
    shopifyCustomerCookie: this.cookies.get(cookieKeys.shopifyCustomerToken.key) ?? undefined,
  })
  private apolloClient: ApolloClient<NormalizedCacheObject>

  constructor({ apolloClient }: { apolloClient: ApolloClient<NormalizedCacheObject> }) {
    this.apolloClient = apolloClient
  }

  /**
   * Helper that builds Iterable attribution values.
   *
   * Reads cookies to determine if a user originated from an Iterable SMS or Email campaign.
   */
  private get _iterableAttributionValues(): { key: string; value: string }[] {
    const iterableKeys = [
      IterableKeys.CAMPAIGN_ID,
      IterableKeys.MESSAGE_ID,
      IterableKeys.TEMPLATE_ID,
      IterableKeys.USER_ID,
      IterableKeys.SMS_CAMPAIGN_ID,
    ]
    const values: { key: string; value: string }[] = []

    iterableKeys.forEach(key => {
      const value = this.cookies.get(key) ?? ''

      // currently we map both email and sms campaigns to the same checkout attribute but sms takes precedence
      if (key === IterableKeys.SMS_CAMPAIGN_ID) {
        key = IterableKeys.CAMPAIGN_ID
      }

      values.push({ key, value })
    })

    return values
  }

  /**
   * Helper that builds custom attributes.
   *
   * Reads cookies and session storage to determine if a customer is logged in,
   * obtains the current location, and appends any iterable attribution values.
   */
  private get _customAttributes(): CustomAttribute[] {
    const isLoggedIn = !!this.account
    const iterableValues = this._iterableAttributionValues
    const currentLocation =
      globalThis.document.location.protocol +
      '//' +
      globalThis.document.location.hostname +
      globalThis.document.location.pathname +
      globalThis.document.location.search
    const landingLocation = globalThis.sessionStorage?.getItem(sessionStorageKeys.landingPage)
    const staticCustomAttributes: CustomAttribute[] = [{ key: 'appSource', value: 'web' }]

    const customAttributes: CustomAttribute[] = [
      ...staticCustomAttributes,
      {
        key: 'landing_site',
        value: landingLocation ?? currentLocation,
      },
      {
        key: 'is_customer_logged_in',
        value: String(isLoggedIn),
      },
    ]
    iterableValues.forEach(item => {
      if (item.value) {
        customAttributes.push(item)
      }
    })
    return customAttributes
  }

  /**
   * Helper that reports userErrors and GraphQL Errors to DataDog.
   *
   * @param sourceFunction the function providing the errors to process
   * @param errorContext the context for the errors being processed
   */
  private reportErrors(sourceFunction: string, errorContext: ErrorContext): void {
    if (errorContext.userErrors?.length) {
      errorContext.userErrors.forEach(err => {
        if (err.code === 'INVALID') {
          reportClientError({
            error: new Error(`${sourceFunction} :: invalid merchandise in cart`),
            context: {
              ...errorContext,
              label: 'error code `INVALID` received from app-figs-cart-service',
            },
          })
        } else if (err.code === 'VALIDATION_CUSTOM') {
          reportClientError({
            error: new Error(`${sourceFunction} :: custom checkout validation error`),
            context: {
              ...errorContext,
              label: 'error code `VALIDATION_CUSTOM` received from app-figs-cart-service',
            },
          })
        } else if (err.code === 'INVALID_MERCHANDISE_LINE') {
          reportClientError({
            error: new Error(`${sourceFunction} :: invalid merchandiseId passed`),
            context: {
              ...errorContext,
              label: 'invalid merchandiseId passed on cart line',
            },
          })
        } else if (err.message.includes("'gid://shopify/ProductVariant/'")) {
          reportClientError({
            error: new Error(`${sourceFunction} :: empty merchandiseId passed`),
            context: {
              ...errorContext,
              label: 'empty merchandiseId passed on cart line',
            },
          })
        } else {
          reportClientError({
            error: new Error(`${sourceFunction} :: unknown userError occurred`),
            context: {
              ...errorContext,
              label: 'unknown userError received from app-figs-cart-service',
            },
          })
        }
      })
    } else if (errorContext.errors?.length) {
      reportClientError({
        error: new Error(`${sourceFunction} :: GraphQL errors occurred`),
        context: {
          ...errorContext,
          label: `${sourceFunction} errors`,
        },
      })
    }
  }

  /**
   * Helper that filters out invalid cart items based on provided errors.
   *
   * @param items items to be filtered
   * @param errors errors to filter `items` by
   * @returns `Array` of filtered `items`
   */
  private _filterInvalidCartItemsOnError<T>(items: T[], errors: Array<CartUserError>): T[] {
    const validItems: T[] = []
    const invalidIndices: number[] = []
    const errorCodesToHandle: String[] = ['NOT_ENOUGH_IN_STOCK', 'PRODUCT_NOT_AVAILABLE']

    const normalizedUserErrors = errors.map(e => ({
      __typename: e.__typename,
      code: e.code ?? 'UNKNOWN_ERROR',
      field: (e.field ?? []).filter(f => f !== null),
      message: e.message,
    }))

    normalizedUserErrors.forEach(err => {
      if (errorCodesToHandle.includes(err.code) || err.code.startsWith('INVALID')) {
        invalidIndices.push(Number.parseInt(err.field[2]!))
      }
    })

    items.forEach((item: T, index: number) => {
      if (!invalidIndices.includes(index)) validItems.push(item)
    })
    return validItems
  }

  /**
   * Helper that performs the `CHECKOUT_CART_CREATE` mutation.
   *
   * @param items items to add to the cart/checkout
   * @param discountCodes discount codes to apply to the cart/checkout
   * @param globalCustomAttributes attribute metadata to add to the cart/checkout
   * @throws an error if no data is returned.
   * @returns resulting cart or errors
   */
  private async _createCheckoutFromCartService(
    items: CheckoutLineItemInput[],
    discountCodes?: string[],
    globalCustomAttributes?: CustomAttribute[]
  ): Promise<FetchResult<CheckoutCartCreateMutation>> {
    const cartLineItems: CartLineInput[] = items.map(item => ({
      merchandiseId: item.variantId.replace('gid://shopify/ProductVariant/', ''),
      quantity: item.quantity,
      attributes: (item.customAttributes ?? []).map(attr => ({
        key: attr.key,
        value: String(attr.value),
      })),
    }))

    const result = await this.apolloClient.mutate({
      mutation: CHECKOUT_CART_CREATE,
      variables: {
        input: {
          lines: cartLineItems,
          discountCodes: discountCodes,
          attributes: globalCustomAttributes,
        },
      },
      context: {
        retryOnFailure: true,
      },
    })

    return result
  }

  /**
   * Creates a checkout via the cart service.
   *
   * It delegates to `_createCheckoutFromCartService`, performs error checking,
   * and appends additional query parameters (such as discount codes and `FBCLID`).
   *
   * @param items items to add to the cart/checkout
   * @param discountCodes discount codes to apply to the cart/checkout
   * @param globalCustomAttributes attribute metadata to add to the cart/checkout
   * @throws an error if no data is returned, or if checkout creation fails
   * @returns `checkoutId` and `checkoutUrl` as `string`
   */
  public async createCheckoutWithCartItems({
    items,
    discountCodes,
    globalCustomAttributes,
  }: Parameters<ICheckoutClient['createCheckoutWithCartItems']>[0]): ReturnType<
    ICheckoutClient['createCheckoutWithCartItems']
  > {
    const customAttributes = [...this._customAttributes, ...(globalCustomAttributes || [])]
    let { data, errors } = await this._createCheckoutFromCartService(
      items,
      discountCodes,
      customAttributes
    )

    // Define error context properties that should be captured across all reported checkout errors
    const sharedErrorContext: ErrorContext = {
      scope: 'checkout',
      errors,
      userErrors: data?.cartCreate.userErrors,
    }

    // report any userErrors or GraphQL errors that are present
    this.reportErrors('createCheckoutWithCartItems', sharedErrorContext)

    // if we have `data` and `userErrors`, we should attempt to fix the cart and retry
    if (data?.cartCreate.userErrors && data.cartCreate.userErrors.length > 0) {
      // FIXME: cart-service should be handling the validation of `userErrors` and the retry
      const validatedLines = this._filterInvalidCartItemsOnError(items, data.cartCreate.userErrors)

      if (validatedLines.length > 0) {
        ;({ data, errors } = await this._createCheckoutFromCartService(
          validatedLines,
          discountCodes,
          customAttributes
        ))

        // Update `errors` and `userErrors` in shared context after retry
        sharedErrorContext.errors = errors
        sharedErrorContext.userErrors = data?.cartCreate.userErrors

        // update `scope` so we know that a retry occurred
        sharedErrorContext.scope = 'checkout (retry)'

        // report any userErrors or GraphQL errors that are present post-retry
        this.reportErrors('createCheckoutWithCartItems', sharedErrorContext)
      }
    }

    if (!data) {
      reportClientError({
        error: new Error(
          'checkoutCartCreate :: no data returned from CHECKOUT_CART_CREATE mutation'
        ),
        context: { ...sharedErrorContext, label: 'checkoutCartCreate missing data' },
      })
      throw new Error('Checkout creation failed: missing data.')
    }

    const shopifyCart = data.cartCreate.cart
    if (!shopifyCart?.id) {
      reportClientError({
        error: new Error('checkoutCartCreate :: no cart ID in response'),
        context: { ...sharedErrorContext, label: 'checkoutCartCreate missing cart ID' },
      })
      throw new Error('Checkout creation failed: missing cart ID.')
    }

    const checkoutString = shopifyCart.checkoutUrl
    if (!checkoutString) {
      reportClientError({
        error: new Error('checkoutCartCreate :: no checkoutUrl in response'),
        context: { ...sharedErrorContext, label: 'checkoutCartCreate missing checkoutUrl' },
      })
      throw new Error('Checkout creation failed: missing checkoutUrl.')
    }

    const cartId = shopifyCart.id
    const checkoutUrl = new URL(checkoutString)

    const discountCode = globalThis.sessionStorage?.getItem(sessionStorageKeys.discount)
    if (discountCode) {
      checkoutUrl.searchParams.append('discount', discountCode)
    }

    if (discountCodes && discountCodes.length > 0) {
      checkoutUrl.searchParams.append('discount', discountCodes[0]!)
    }

    const facebookCookieVal = this.cookies.get(cookieKeys.facebook.key)
    if (facebookCookieVal) {
      checkoutUrl.searchParams.append('FBCLID', facebookCookieVal)
    }

    return { checkoutUrl, checkoutId: cartId }
  }

  /**
   * Checks if a given cart has completed checkout.
   *
   * If lookup succeeds, we will get a populated `Cart` back indicating the cart is still active.
   * Otherwise, we will receive an `null` response indicating the cart/checkout is completed.
   *
   * @param cartId the id of the cart to check the status of
   * @returns whether or not the given cart has completed checkout
   */
  public async isCheckoutCompleted(
    cartId: Parameters<ICheckoutClient['isCheckoutCompleted']>[0]
  ): ReturnType<ICheckoutClient['isCheckoutCompleted']> {
    const { data, error } = await this.apolloClient.query({
      query: FETCH_CART_EXISTS,
      variables: {
        cartId: cartId,
      },
      fetchPolicy: 'network-only',
    })
    if (error) throw error
    return data.cart.cart === null
  }

  /**
   * Retrieves a cart by id.
   *
   * @param cartId the id of the cart to retrieve
   * @throws error from `ApolloClient`, if present
   * @returns the found cart
   */
  public async fetchCart(
    cartId: Parameters<ICheckoutClient['fetchCart']>[0]
  ): Promise<Omit<FigsCart, 'checkoutUrl'> | null> {
    const { data, error } = await this.apolloClient.query({
      query: FETCH_CART,
      variables: {
        cartId: cartId,
      },
      fetchPolicy: 'network-only',
    })
    if (error) throw error
    return data.cart?.cart
  }

  /**
   * Create a cart, optionally accepting props for attributes, line items, etc.
   *
   * @param props.attributes cart attributes to set on the created cart
   * @param props.customerAccessToken a token used to associate the cart with a user
   * @param props.discountCodes discount codes to apply to the created cart
   * @param props.giftCardCodes gift cards to apply to the created cart
   * @param props.lines line items to add to the created cart
   * @param props.metafields metafields to apply to set on the created cart
   * @param props.note a note to pass/apply to the created cart/checkout
   * @throws an error if no data is returned, or if cart creation fails
   * @returns the created cart if successful, `null` otherwise
   */
  public async createCart(
    props: Parameters<ICheckoutClient['createCart']>[0]
  ): ReturnType<ICheckoutClient['createCart']> {
    // standardize lines, specifically `merchandiseId` format
    const standardizedLines = props.lines.map(line => ({
      ...line,
      merchandiseId: line.merchandiseId.replace('gid://shopify/ProductVariant/', ''),
    }))

    // attempt to create cart
    let { data, errors } = await this.apolloClient.mutate({
      mutation: CART_CREATE,
      variables: {
        input: {
          ...props,
          lines: standardizedLines,
        },
      },
      context: {
        retryOnFailure: true,
      },
    })

    const sharedErrorContext: ErrorContext = {
      scope: 'cart',
      errors,
      userErrors: data?.cartCreate.userErrors,
    }

    // report any userErrors or GraphQL errors that are present
    this.reportErrors('createCart', sharedErrorContext)

    // if we have `data` and `userErrors`, we should attempt to fix the cart and retry
    if (data?.cartCreate.userErrors && data.cartCreate.userErrors.length > 0) {
      // FIXME: cart-service should be handling the validation of `userErrors` and the retry
      const validatedLines = this._filterInvalidCartItemsOnError(
        standardizedLines,
        data.cartCreate.userErrors
      )

      // trying again, passing `validatedLines`
      ;({ data, errors } = await this.apolloClient.mutate({
        mutation: CART_CREATE,
        variables: {
          input: {
            ...props,
            lines: validatedLines,
          },
        },
        context: {
          retryOnFailure: true,
        },
      }))

      // Update `errors` and `userErrors` in shared context after retry
      sharedErrorContext.errors = errors
      sharedErrorContext.userErrors = data?.cartCreate.userErrors

      // update `scope` so we know that a retry occurred
      sharedErrorContext.scope = 'cart (retry)'

      // report any userErrors or GraphQL errors that are present post-retry
      this.reportErrors('createCart', sharedErrorContext)
    }

    if (!data) {
      reportClientError({
        error: new Error('cartCreate :: no data returned from CART_CREATE mutation'),
        context: {
          ...sharedErrorContext,
          label: 'cartCreate missing data',
        },
      })
      throw new Error('Cart creation failed: missing data.')
    }

    if (!data?.cartCreate.cart?.id) {
      reportClientError({
        error: new Error('cartCreate :: no cart ID in response'),
        context: {
          label: 'cartCreate missing cart ID',
          ...sharedErrorContext,
        },
      })
      throw new Error('Cart creation failed: missing cart ID.')
    }

    return data.cartCreate.cart
  }

  /**
   * Add line items to the provided cart
   * @param props.cartId the id of the cart to add lines to
   * @param props.lines the lines to add to the cart
   * @returns the updated cart if successful, `null` otherwise
   */
  public async addLines({
    cartId,
    lines,
  }: Parameters<ICheckoutClient['addLines']>[0]): ReturnType<ICheckoutClient['addLines']> {
    // standardize lines, specifically `merchandiseId` format
    const standardizedLines = lines.map(line => ({
      ...line,
      merchandiseId: line.merchandiseId.replace('gid://shopify/ProductVariant/', ''),
    }))

    let { data, errors } = await this.apolloClient.mutate({
      mutation: CART_LINES_ADD,
      variables: {
        cartId,
        lines: standardizedLines,
      },
      context: {
        retryOnFailure: true,
      },
    })

    const sharedErrorContext: ErrorContext = {
      scope: 'cart',
      errors,
      userErrors: data?.cartLinesAdd.userErrors,
    }

    // report any userErrors or GraphQL errors that are present
    this.reportErrors('addLines', sharedErrorContext)

    // if we have `data` and `userErrors`, we should attempt to fix the cart and retry
    if (data?.cartLinesAdd.userErrors && data.cartLinesAdd.userErrors.length > 0) {
      // FIXME: cart-service should be handling the validation of `userErrors` and the retry
      const validatedLines = this._filterInvalidCartItemsOnError(
        standardizedLines,
        data.cartLinesAdd.userErrors
      )

      // trying again, passing `validatedLines`
      ;({ data, errors } = await this.apolloClient.mutate({
        mutation: CART_LINES_ADD,
        variables: {
          cartId,
          lines: validatedLines,
        },
        context: {
          retryOnFailure: true,
        },
      }))

      // Update `errors` and `userErrors` in shared context after retry
      sharedErrorContext.errors = errors
      sharedErrorContext.userErrors = data?.cartLinesAdd.userErrors

      // update `scope` so we know that a retry occurred
      sharedErrorContext.scope = 'cart (retry)'

      // report any userErrors or GraphQL errors that are present post-retry
      this.reportErrors('addLines', sharedErrorContext)
    }

    if (!data) {
      reportClientError({
        error: new Error('addLines :: no data returned from CART_LINES_ADD mutation'),
        context: {
          ...sharedErrorContext,
          label: 'addLines missing data',
        },
      })
      throw new Error('cart lines add failed: missing data.')
    }

    if (!data?.cartLinesAdd.cart?.id) {
      reportClientError({
        error: new Error('addLines :: No cart ID in response'),
        context: {
          ...sharedErrorContext,
          label: 'addLines missing cart ID',
        },
      })
      throw new Error('cart lines add failed: missing cart ID.')
    }

    return data.cartLinesAdd.cart
  }

  /**
   * Update line items in the provided cart
   * @param props.cartId the id of the cart to update lines on
   * @param props.lines the lines to update in the cart
   * @returns the updated cart if successful, `null` otherwise
   */
  public async updateLines({
    cartId,
    lines,
  }: Parameters<ICheckoutClient['updateLines']>[0]): ReturnType<ICheckoutClient['updateLines']> {
    let { data, errors } = await this.apolloClient.mutate({
      mutation: CART_LINES_UPDATE,
      variables: {
        cartId,
        lines,
      },
      context: {
        retryOnFailure: true,
      },
    })

    const sharedErrorContext: ErrorContext = {
      scope: 'cart',
      errors,
      userErrors: data?.cartLinesUpdate.userErrors,
    }

    // report any userErrors or GraphQL errors that are present
    this.reportErrors('updateLines', sharedErrorContext)

    // if we have `data` and `userErrors`, we should attempt to fix the cart and retry
    if (data?.cartLinesUpdate.userErrors && data.cartLinesUpdate.userErrors.length > 0) {
      // FIXME: cart-service should be handling the validation of `userErrors` and the retry
      const validatedLines = this._filterInvalidCartItemsOnError(
        lines,
        data.cartLinesUpdate.userErrors
      )

      // trying again, passing `validatedLines`
      ;({ data, errors } = await this.apolloClient.mutate({
        mutation: CART_LINES_UPDATE,
        variables: {
          cartId,
          lines: validatedLines,
        },
        context: {
          retryOnFailure: true,
        },
      }))

      // Update `errors` and `userErrors` in shared context after retry
      sharedErrorContext.errors = errors
      sharedErrorContext.userErrors = data?.cartLinesUpdate.userErrors

      // update `scope` so we know that a retry occurred
      sharedErrorContext.scope = 'cart (retry)'

      // report any userErrors or GraphQL errors that are present post-retry
      this.reportErrors('updateLines', sharedErrorContext)
    }

    if (!data) {
      reportClientError({
        error: new Error('updateLine :: no data returned from CART_LINES_UPDATE mutation'),
        context: {
          ...sharedErrorContext,
          label: 'updateLines missing data',
        },
      })
      throw new Error('cart lines update failed: missing data.')
    }

    if (!data?.cartLinesUpdate.cart?.id) {
      reportClientError({
        error: new Error('updateLines :: no cart ID in response'),
        context: {
          ...sharedErrorContext,
          label: 'updateLines missing cart id',
        },
      })
      throw new Error('cart lines update failed: missing cart ID.')
    }

    return data.cartLinesUpdate.cart
  }

  /**
   * Update cart-level attributes for the given cart. All updates are full-replacements.
   * @param props.cartId the id of the cart to update the attributes of
   * @param props.attributes the attributes to set on the cart
   * @returns the updated cart if successful, `null` otherwise
   */
  public async updateCartAttributes({
    cartId,
    attributes,
  }: Parameters<ICheckoutClient['updateCartAttributes']>[0]): ReturnType<
    ICheckoutClient['updateCartAttributes']
  > {
    let { data, errors } = await this.apolloClient.mutate({
      mutation: CART_ATTRIBUTES_UPDATE,
      variables: {
        cartId,
        attributes,
      },
      context: {
        retryOnFailure: true,
      },
    })

    const sharedErrorContext = {
      scope: 'cart',
      errors,
      userErrors: data?.cartAttributesUpdate.userErrors,
    }

    // report any userErrors or GraphQL errors that are present
    this.reportErrors('updateCartAttributes', sharedErrorContext)

    if (!data) {
      reportClientError({
        error: new Error(
          'updateCartAttributes :: no data returned from CART_ATTRIBUTES_UPDATE mutation'
        ),
        context: {
          ...sharedErrorContext,
          label: 'updateCartAttributes missing data',
        },
      })
      throw new Error('cart attributes update failed: missing data.')
    }

    if (!data?.cartAttributesUpdate.cart?.id) {
      reportClientError({
        error: new Error('updateCartAttributes :: no cart ID in response'),
        context: {
          ...sharedErrorContext,
          label: 'updateCartAttributes missing cart ID',
        },
      })
      throw new Error('cart attributes update failed: missing cart ID.')
    }

    return data.cartAttributesUpdate.cart
  }

  /**
   * Retrieves the `checkoutUrl` for a given cart by id
   * @param cartId the id of the cart to retrieve a `checkoutUrl` for
   * @param queryParams query params to append to the `checkoutUrl` for discounts, 3rd party tracking, etc.
   * @throws ApolloError
   * @returns `checkoutUrl` as string if present, else null
   */
  public async fetchCheckoutUrl(
    cartId: Parameters<ICheckoutClient['fetchCheckoutUrl']>[0],
    queryParams?: Parameters<ICheckoutClient['fetchCheckoutUrl']>[1]
  ): ReturnType<ICheckoutClient['fetchCheckoutUrl']> {
    let result: string | null = null
    const { data, errors } = await this.apolloClient.query({
      query: FETCH_CHECKOUT_URL,
      variables: {
        cartId,
      },
      fetchPolicy: 'network-only',
    })

    const sharedErrorContext: ErrorContext = {
      scope: 'checkout',
      errors,
      userErrors: data.retrieveCheckoutUrl.userErrors,
    }

    // report any userErrors or GraphQL errors that are present
    this.reportErrors('fetchCheckoutUrl', sharedErrorContext)

    if (data.retrieveCheckoutUrl.checkoutUrl) {
      const checkoutUrl = URL.parse(data.retrieveCheckoutUrl.checkoutUrl)
      if (checkoutUrl && queryParams) {
        Object.entries(queryParams).forEach(([key, value]) => {
          checkoutUrl.searchParams.append(key, value)
        })
      }
      if (checkoutUrl) result = checkoutUrl.toString()
    } else {
      reportClientError({
        error: new Error('fetchCheckoutUrl :: no data returned from FETCH_CHECKOUT_URL query'),
        context: {
          ...sharedErrorContext,
          label: 'fetchCheckoutUrl missing data',
        },
      })
      throw new Error('checkout url retrieve failed: missing data.')
    }

    return result
  }
}
